import { LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';
import { takeEvery, call, select, CallEffect, SelectEffect } from 'redux-saga/effects';

import { timeEvent, track } from 'js/sagas/mixpanel/mixpanelProvider';
import { ROUTE_CODES } from 'js/config/routes';
import { migrationError, setConversionComplete, startMigration } from 'js/reducers/legacyProjectMigration';
import { isOverImagesLimit } from 'js/home/MigrateProject/utils';
import { CurrentUser, RootState } from 'js/types';
import { getUserWithPermissions } from 'js/permissions/permissionsHelpers';
import { projectDurationLimitMs } from 'js/shared/helpers/projectDurationLimit';
import { toMilliseconds, toSeconds } from 'js/editor/PlayerContext/timingHelpers';
import { MAX_SCRIBE_LENGTH_20M_IN_MS } from 'js/config/defaults';

const MIGRATION_SUCCEEDED_EVENT_NAME = 'Legacy Migration Succeeded';

function* trackVisitMigrateLegacyProject(action: LocationChangeAction) {
  if (action.payload.location.pathname === ROUTE_CODES.MIGRATE_SCRIBE) {
    yield call(track, 'Visit Migrate Legacy Project');
  }
}

function* trackStartConversion() {
  yield call(track, 'Start Legacy Migration');
  yield call(timeEvent, MIGRATION_SUCCEEDED_EVENT_NAME);
}

function* trackMigrationError(action: ReturnType<typeof migrationError>) {
  yield call(track, 'Legacy Migration Failed', {
    'Failure Reason': action.payload
  });
}

function* trackConversionComplete(
  action: ReturnType<typeof setConversionComplete>
): Generator<CallEffect | SelectEffect, void, CurrentUser> {
  const { convertedProjectDurationSeconds, convertedProject } = action.payload;
  const user = yield select((state: RootState) => state.auth.currentUser);
  const userWithPermissions = getUserWithPermissions(user);
  const projectDurationLimitInMs = projectDurationLimitMs(userWithPermissions);
  const projectDurationLimit = toSeconds(projectDurationLimitInMs);
  const isOverDurationLimit = convertedProjectDurationSeconds && convertedProjectDurationSeconds > projectDurationLimit;
  const isOverMaxDurationLimit = toMilliseconds(convertedProjectDurationSeconds) > MAX_SCRIBE_LENGTH_20M_IN_MS;

  let imageElements = 0;
  let textElements = 0;

  convertedProject.elements.forEach(element => {
    if (element.type === 'Image') {
      imageElements++;
    } else if (element.type === 'Text') {
      textElements++;
    }
  });

  const imageLimitExceeded = isOverImagesLimit(imageElements);

  yield call(track, MIGRATION_SUCCEEDED_EVENT_NAME, {
    'Duration Limit Exceeded': isOverDurationLimit,
    'Image Limit Exceeded': imageLimitExceeded,
    'Max Duration Limit Exceeded': isOverMaxDurationLimit,
    'Number of Image Elements': imageElements,
    'Number of Text Elements': textElements,
    'Number of Audio Clips': convertedProject.audioClips?.length || 0,
    'Scribe ID': convertedProject.id,
    'Scribe Length (seconds)': convertedProjectDurationSeconds
  });
}

export default function* trackMigrateLegacyProjectSagas() {
  yield takeEvery(LOCATION_CHANGE, trackVisitMigrateLegacyProject);
  yield takeEvery(startMigration.type, trackStartConversion);
  yield takeEvery(migrationError.type, trackMigrationError);
  yield takeEvery(setConversionComplete.type, trackConversionComplete);
}
